import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { X } from 'lucide-react';

export default function AdminReservations() {
  const { getAuthHeaders } = useAuth();
  const [reservations, setReservations] = useState([]);

  useEffect(() => { fetchReservations(); }, []);

  const fetchReservations = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/reservations`,
        { headers: getAuthHeaders() }
      );
      setReservations(response.data);
    } catch (error) { console.error('Erreur:', error); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Annuler cette réservation ?')) return;
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/reservations/${id}/cancel`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchReservations();
    } catch (error) { console.error('Erreur:', error); }
  };

  const formatDate = (d) => new Date(d + 'T12:00:00').toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric'
  });

  return (
    <AdminLayout>
      <div>
        <h1 className="text-2xl sm:text-3xl font-serif mb-6 sm:mb-8">Réservations</h1>

        {/* Desktop / Tablette large : tableau */}
        <div className="hidden lg:block bg-white border border-border rounded-sm overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-secondary">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Heure</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Client</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Prestation</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Contact</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Statut</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {reservations.map((resa) => (
                <tr key={resa.id}>
                  <td className="px-4 py-3 text-sm">{formatDate(resa.date)}</td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">{resa.heure_debut?.substring(0,5)} - {resa.heure_fin?.substring(0,5)}</td>
                  <td className="px-4 py-3 text-sm font-medium">{resa.nom_client}</td>
                  <td className="px-4 py-3 text-sm">{resa.prestation_nom}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs">
                      <div>{resa.email_client}</div>
                      <div className="text-muted-foreground">{resa.telephone_client}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 text-xs rounded ${
                      resa.statut === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {resa.statut === 'confirmed' ? 'Confirmé' : 'Annulé'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {resa.statut === 'confirmed' && (
                      <button onClick={() => handleCancel(resa.id)} className="text-red-600 hover:text-red-800 p-1">
                        <X size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile / Tablette : cartes */}
        <div className="lg:hidden space-y-3">
          {reservations.map((resa) => (
            <div key={resa.id} className="bg-white border border-border rounded-sm p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-medium text-sm">{resa.nom_client}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{resa.prestation_nom}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`inline-block px-2 py-1 text-xs rounded ${
                    resa.statut === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {resa.statut === 'confirmed' ? 'Confirmé' : 'Annulé'}
                  </span>
                  {resa.statut === 'confirmed' && (
                    <button onClick={() => handleCancel(resa.id)} className="text-red-600 hover:text-red-800 p-1 border border-red-200 rounded-sm">
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium text-foreground">Date</span>
                  <p>{formatDate(resa.date)}</p>
                </div>
                <div>
                  <span className="font-medium text-foreground">Heure</span>
                  <p>{resa.heure_debut?.substring(0,5)} - {resa.heure_fin?.substring(0,5)}</p>
                </div>
                <div>
                  <span className="font-medium text-foreground">Email</span>
                  <p className="break-all">{resa.email_client}</p>
                </div>
                <div>
                  <span className="font-medium text-foreground">Téléphone</span>
                  <p>{resa.telephone_client}</p>
                </div>
              </div>
            </div>
          ))}
          {reservations.length === 0 && (
            <div className="bg-white border border-border rounded-sm p-8 text-center text-muted-foreground text-sm">
              Aucune réservation.
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
